import {
	Vue, Component, Ref, Prop, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import CurrencySalesTextLink from 'components/Tabulator/CurrencySalesTextLink';
import { httpClient } from 'utils/http';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import { useRouter } from 'vue-router';
import Template from './template.vue';

@Component({
	components: {
		CurrencySalesTextLink,
	},
	mixins: [Template],
})
class PaidOrders extends Vue {
	@Ref('paidOrdersTable')
	private allTableReference!: HTMLDivElement;

	@Prop({
		type: String,
		required: true,
	})
	private readonly routeId!: string;

	private table?: Tabulator;

	private isLoading = false;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private router = useRouter();

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'id',
				title: 'Order ID',
				formatter: (cell) => {
					const route = {
						name: 'showOrder',
						params: { orderid: cell.getValue() },
					};
					const link = document.createElement('a');
					link.href = this.router.resolve(route).href;
					link.innerText = cell.getValue();
					link.addEventListener(
						'click',
						(e) => {
							e.preventDefault();
							this.router.push(route);
						},
					);
					return link;
				},
			},
			{
				title: 'Date',
				field: 'dateadded',
			},
			{
				title: 'Sales Value',
				field: 'salesvalue',
				formatter: (cell: CellComponentExtended<DB.OrderItemModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: CurrencySalesTextLink,
						props: {
							data,
							salesvalue: true,
						},
					});
					return instance;
				},
			},
			{
				title: 'Discount',
				field: 'discount_voucher',
				formatter: (cell: CellComponentExtended<DB.OrderItemModel>) => {
					const data = cell.getData();
					const instance = createInstance({
						component: CurrencySalesTextLink,
						props: {
							data,
							discount_voucher: true,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount() {
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this?.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.allTableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const id = this.routeId;
		const parameter = new URLSearchParams({
			limit: '0',
			orderby: 'id DESC',
			where: JSON.stringify({
				userid: parseInt(
					id,
					10,
				),
				paid: 1,
			}),
		});

		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get<DB.OrderModel[]>(`/api/order?${parameter}`);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}
}

export default toNative(PaidOrders);
